import { useQuery, useQueryClient } from '@tanstack/react-query';
import backend from '../../services/backend';
import { finalizePollInterval, finalizeRequestedKey, type FinalizeMarker } from './finalizePoll';

export type SupervisorFindingsResponse = Awaited<ReturnType<typeof backend.getProjectSupervisorFindings>>['data'];

export const findingsKey = (projectId: string | undefined) => ['getProjectSupervisorFindings', projectId] as const;

const fetchFindings = async (projectId: string | undefined): Promise<SupervisorFindingsResponse> =>
  (await backend.getProjectSupervisorFindings(projectId as string)).data;

/**
 * The project's supervisor findings and `pendingFinalizeCount`. Two observers share the query: the
 * Corrections tab (the list) and the project Header (admins only, for the Finalize item's busy state).
 * The Header is mounted on every project tab, so the finalize poll below keeps running after the admin
 * leaves the Corrections tab. Undefined `projectId` disables the query.
 */
export function useSupervisorFindings(projectId: string | undefined) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: findingsKey(projectId),
    queryFn: () => fetchFindings(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
    // Re-evaluated after every fetch: polls only after a finalize, until its handler lands.
    refetchInterval: (query) =>
      projectId
        ? finalizePollInterval(
            query.state.data?.finalizedAt,
            queryClient.getQueryData<FinalizeMarker | null>(finalizeRequestedKey(projectId)),
            Date.now(),
          )
        : false,
  });
}
